/**
 * All group interaction lives here.
 *
 * The overlay listens for mousedown on the slide root in the CAPTURE phase. React 17
 * dispatches onMouseDown/onClick from the root container during bubbling, so this
 * listener always runs first and can claim an event before the element's own handler
 * sees it. When the event is not ours we do nothing at all and it proceeds down its
 * existing path untouched — that is what keeps single-element behaviour identical.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import classes from './GroupSelectionOverlay.module.css';
import { useGroupSelection } from './useGroupSelection';
import { useGroupCommands } from './useGroupCommands';
import { readGroups } from './groupPersistence';
import {
  memberBox, computeGroupBounds, isFullyEnclosed, groupForMember, translateBox,
  scaleBoxInto, isResizeAllowed, boxToStyles, pruneMember,
} from './groupModel';
import { commitElementStyle } from '../../utils/commitElementStyle';
import { parseStyleAttribute } from '../../utils/elementStyleUtils';
import { saveMirrorToCloudinary } from '../../store/slideSlice';
import { getSnapshot } from '../../utils/utils';

const SLIDE_ROOT_ID = 'slide01';

/** Every selectable object on the slide, as { id, box }. */
const collectMembers = (slideData) => {
  if (!slideData) return [];
  const sources = [
    slideData.textBox, slideData.image, slideData.shapes,
    slideData.wordArtTextObjects, slideData.smartArtObjects,
  ];
  return sources.flatMap((map) => (
    Object.keys(map || {}).map((id) => ({ id, box: memberBox(map[id].styles) }))
  ));
};

/**
 * Walk up from the event target to the slide object that owns it.
 *
 * Matching against the known member ids rather than the nearest `[id]` ancestor
 * matters: Quill wraps shape and textbox content in its own elements that carry ids,
 * so a naive closest('[id]') would resolve a click to the editor rather than the shape.
 */
/** The eight handles, as fractions of the group box plus which edges each one moves. */
const HANDLES = [
  { name: 'top-left', fx: 0, fy: 0, dx: -1, dy: -1, cursor: 'nwse-resize' },
  { name: 'top', fx: 0.5, fy: 0, dx: 0, dy: -1, cursor: 'ns-resize' },
  { name: 'top-right', fx: 1, fy: 0, dx: 1, dy: -1, cursor: 'nesw-resize' },
  { name: 'right', fx: 1, fy: 0.5, dx: 1, dy: 0, cursor: 'ew-resize' },
  { name: 'bottom-right', fx: 1, fy: 1, dx: 1, dy: 1, cursor: 'nwse-resize' },
  { name: 'bottom', fx: 0.5, fy: 1, dx: 0, dy: 1, cursor: 'ns-resize' },
  { name: 'bottom-left', fx: 0, fy: 1, dx: -1, dy: 1, cursor: 'nesw-resize' },
  { name: 'left', fx: 0, fy: 0.5, dx: -1, dy: 0, cursor: 'ew-resize' },
];

/** Apply a pointer delta to the edges a handle controls, giving the proposed bounds. */
const resizeBounds = (from, handle, dx, dy) => {
  let { left, top, width, height } = from;
  if (handle.dx === -1) { left += dx; width -= dx; }
  if (handle.dx === 1) { width += dx; }
  if (handle.dy === -1) { top += dy; height -= dy; }
  if (handle.dy === 1) { height += dy; }
  return { left, top, width, height };
};

/**
 * Remove an element's single-selection chrome.
 *
 * Deliberately not use-resizer's clearAllSlideSelections: that dispatches
 * SLIDE_SELECTION_CLEAR, which useGroupSelection listens for and treats as "drop the
 * multi-selection" — the opposite of what seeding needs.
 */
const stripSelectionChrome = (el) => {
  el.classList.remove('selected');
  el.querySelectorAll('.resizer').forEach((handle) => handle.remove());
};

const resolveMemberId = (target, memberIds) => {
  let el = target;
  while (el && el.id !== SLIDE_ROOT_ID) {
    if (el.id && memberIds.has(el.id)) return el.id;
    el = el.parentElement;
  }
  return null;
};

export const GroupSelectionOverlay = () => {
  const currentSlideId = useSelector((s) => s.slide.currentSlideId);
  const slideData = useSelector((s) => currentSlideId && s.slide.slides[currentSlideId]);
  const presentMode = useSelector((s) => s.slide.presentMode);
  const isReadOnly = useSelector((s) => s.slide.isReadOnly);
  const isMasterSlide = useSelector((s) => s.slide.isMasterSlide);
  const existingSlides = useSelector((s) => s.slide.slides);
  const mirrorSlides = useSelector((s) => s.slide.mirrorSlides);
  const dispatch = useDispatch();
  const { id: presentationId } = useParams();

  const { selectedIds, activeGroupId, toggle, select, selectGroup, clear } = useGroupSelection();
  const [marquee, setMarquee] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [liveDelta, setLiveDelta] = useState(null);
  const dragRef = useRef(null);
  const [resizing, setResizing] = useState(false);
  const [liveBounds, setLiveBounds] = useState(null);
  const resizeRef = useRef(null);
  // Read inside the capture listener without making it re-subscribe on every
  // selection change.
  const selectionRef = useRef(selectedIds);
  selectionRef.current = selectedIds;
  // Set when we claim a mousedown, so the click that follows it can be claimed too.
  const claimedClickRef = useRef(false);

  const groups = useMemo(() => readGroups(slideData), [slideData]);
  const members = useMemo(() => collectMembers(slideData), [slideData]);
  const memberIds = useMemo(() => new Set(members.map((m) => m.id)), [members]);
  const disabled = presentMode || isReadOnly || isMasterSlide;

  const selected = useMemo(
    () => members.filter((m) => selectedIds.includes(m.id)),
    [members, selectedIds]
  );
  const bounds = useMemo(
    () => computeGroupBounds(selected.map((m) => m.box)),
    [selected]
  );

  /** Convert a page-space point into slide percentages. */
  const toSlidePercent = useCallback((clientX, clientY) => {
    const rect = document.getElementById(SLIDE_ROOT_ID)?.getBoundingClientRect();
    if (!rect || !rect.width || !rect.height) return null;
    return {
      x: ((clientX - rect.left) / rect.width) * 100,
      y: ((clientY - rect.top) / rect.height) * 100,
    };
  }, []);

  const { doGroup, doUngroup, canGroup, canUngroup, persistGroups } = useGroupCommands();

  // Ctrl+G groups, Ctrl+Shift+G ungroups. Registered on document so it works wherever
  // focus sits, and it claims the keystroke only when it actually acts — otherwise the
  // event carries on to whatever else may want it.
  useEffect(() => {
    if (disabled) return undefined;

    const onKeyDown = (e) => {
      const isGroupKey = (e.ctrlKey || e.metaKey) && (e.key === 'g' || e.key === 'G');
      if (!isGroupKey) return;
      if (e.shiftKey ? !canUngroup : !canGroup) return;
      e.preventDefault();
      e.stopPropagation();
      if (e.shiftKey) doUngroup();
      else doGroup();
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [disabled, canGroup, canUngroup, doGroup, doUngroup]);

  useEffect(() => {
    const slide = document.getElementById(SLIDE_ROOT_ID);
    if (!slide || disabled) return undefined;

    const onMouseDownCapture = (e) => {
      // A right-click inside an existing multi-selection must leave it intact — the
      // context menu needs it to still be there to offer Group. Shape and Image bind
      // dragEvent to onMouseDown without checking the button, so an unclaimed
      // right-click starts a drag whose mouseup calls addResizer, and that clears the
      // multi-selection. Claiming the mousedown stops that; the contextmenu event is
      // dispatched separately and still fires.
      if (e.button === 2) {
        const rightId = resolveMemberId(e.target, memberIds);
        if (rightId && selectionRef.current.includes(rightId)) e.stopPropagation();
        return;
      }
      if (e.button !== 0) return;
      claimedClickRef.current = false;

      // The overlay's own controls sit inside the slide, so this capture listener runs
      // on the way down to them — before their React onMouseDown could stop it. Without
      // this guard, clicking the Group button would start a marquee, and the mouseup
      // would clear the selection before onClick ever ran.
      if (e.target.closest?.('[data-group-overlay]')) return;

      const ownerId = resolveMemberId(e.target, memberIds);

      // 1. A member of a group: claim the event, the whole group takes selection.
      const owningGroup = ownerId ? groupForMember(groups, ownerId) : null;
      if (owningGroup) {
        e.stopPropagation();
        e.preventDefault();
        claimedClickRef.current = true;
        const groupMemberIds = groups[owningGroup].memberIds;
        selectGroup(owningGroup, groupMemberIds);
        const start = toSlidePercent(e.clientX, e.clientY);
        if (start) {
          dragRef.current = {
            start,
            origin: members.filter((m) => groupMemberIds.includes(m.id)),
          };
          setDragging(true);
        }
        return;
      }

      // 2. Modifier held over an object: toggle it into the selection. An object
      //    already selected the ordinary way is folded in first, so Ctrl+clicking a
      //    second object extends that selection rather than replacing it.
      if (ownerId && (e.shiftKey || e.ctrlKey || e.metaKey)) {
        e.stopPropagation();
        e.preventDefault();
        claimedClickRef.current = true;
        const domSelected = slide.querySelector('.selected');
        const seedId = domSelected && memberIds.has(domSelected.id) ? domSelected.id : null;
        if (domSelected) stripSelectionChrome(domSelected);
        toggle(ownerId, seedId);
        return;
      }

      // 3. Bare slide background: begin a marquee. The event is deliberately NOT
      //    claimed, so the existing click-to-deselect behaviour still runs.
      if (!ownerId) {
        const start = toSlidePercent(e.clientX, e.clientY);
        if (start) setMarquee({ start, current: start });
        return;
      }

      // 4. A plain click on an ungrouped object is not ours. Do nothing — the event
      //    proceeds to use-resizer exactly as it always has.
    };

    /**
     * Claim the click that follows a mousedown we claimed.
     *
     * stopPropagation on mousedown does not stop the click — they are separate
     * dispatches. Left unclaimed, the click reaches the element's own onClick, which
     * sets its selected state, which calls addResizer, which calls
     * clearAllSlideSelections — and that wipes the multi-selection we just built.
     */
    const onClickCapture = (e) => {
      if (!claimedClickRef.current) return;
      claimedClickRef.current = false;
      e.stopPropagation();
      e.preventDefault();
    };

    slide.addEventListener('mousedown', onMouseDownCapture, true);
    slide.addEventListener('click', onClickCapture, true);
    return () => {
      slide.removeEventListener('mousedown', onMouseDownCapture, true);
      slide.removeEventListener('click', onClickCapture, true);
    };
  }, [groups, members, memberIds, disabled, selectGroup, toggle, toSlidePercent]);

  /**
   * Commit every member that moved, then take one mirror snapshot for the gesture.
   *
   * Styles are written straight onto the elements during the drag and only persisted
   * here — the same reasoning ShapeAdjustHandles documents about not firing an API
   * call and a mirror upload on every mousemove. The slides object is threaded through
   * the loop so the single document save at the end carries every member's change.
   */
  const commitMembers = useCallback(async (origin) => {
    const live = origin
      .map(({ id }) => ({ id, el: document.getElementById(id) }))
      .filter(({ el }) => el);

    let slides = existingSlides;
    live.forEach(({ id, el }, i) => {
      const next = commitElementStyle({
        eleId: id,
        updatedStyle: parseStyleAttribute(el.getAttribute('style')),
        currentSlideId,
        existingSlides: slides,
        mirrorSlides,
        presentationId,
        dispatch,
        persistDocument: i === live.length - 1,
      });
      if (next) slides = next;
    });

    const dataUrl = await getSnapshot();
    dispatch(saveMirrorToCloudinary({
      dataId: currentSlideId,
      mirrorSlideDataUrl: dataUrl,
      presentationId,
    }));
  }, [currentSlideId, existingSlides, mirrorSlides, presentationId, dispatch]);

  useEffect(() => {
    if (!dragging) return undefined;

    const onMove = (e) => {
      const drag = dragRef.current;
      const now = toSlidePercent(e.clientX, e.clientY);
      if (!drag || !now) return;
      const dx = now.x - drag.start.x;
      const dy = now.y - drag.start.y;
      drag.origin.forEach(({ id, box }) => {
        const el = document.getElementById(id);
        if (!el) return;
        const moved = translateBox(box, dx, dy);
        el.style.left = `${moved.left}%`;
        el.style.top = `${moved.top}%`;
      });
      setLiveDelta({ dx, dy });
    };

    const onUp = () => {
      const drag = dragRef.current;
      dragRef.current = null;
      setDragging(false);
      setLiveDelta(null);
      if (drag) commitMembers(drag.origin);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [dragging, commitMembers, toSlidePercent]);

  /**
   * Deletion happens through the existing context menu, which knows nothing about
   * groups. Rather than modify that path — which would breach the zero-diff rule —
   * reconcile here: any member id that no longer exists on the slide is pruned, and a
   * group left with fewer than two members dissolves.
   *
   * The ref guards re-entrancy: writeGroups is async and dispatches, so without it the
   * effect could fire again on the same stale ids before the first write lands.
   */
  const reconcilingRef = useRef(false);
  useEffect(() => {
    if (disabled || !slideData || reconcilingRef.current) return;
    const stale = Object.values(groups)
      .flatMap((g) => g.memberIds)
      .filter((memberId) => !memberIds.has(memberId));
    if (stale.length === 0) return;

    reconcilingRef.current = true;
    const next = stale.reduce((acc, memberId) => pruneMember(acc, memberId), groups);
    Promise.resolve(persistGroups(next)).finally(() => {
      reconcilingRef.current = false;
    });
  }, [groups, memberIds, slideData, disabled, persistGroups]);

  const beginResize = useCallback((e, handle) => {
    e.stopPropagation();
    e.preventDefault();
    const start = toSlidePercent(e.clientX, e.clientY);
    if (!start || !bounds) return;
    resizeRef.current = { handle, start, from: bounds, origin: selected };
    setResizing(true);
  }, [bounds, selected, toSlidePercent]);

  useEffect(() => {
    if (!resizing) return undefined;

    const onMove = (e) => {
      const rs = resizeRef.current;
      const now = toSlidePercent(e.clientX, e.clientY);
      if (!rs || !now) return;
      const to = resizeBounds(rs.from, rs.handle, now.x - rs.start.x, now.y - rs.start.y);
      // Refuse a resize that would crush any member below the minimum, rather than
      // letting it invert or vanish.
      if (!isResizeAllowed(rs.origin.map((o) => o.box), rs.from, to)) return;
      rs.origin.forEach(({ id, box }) => {
        const el = document.getElementById(id);
        if (!el) return;
        Object.assign(el.style, boxToStyles(scaleBoxInto(box, rs.from, to)));
      });
      setLiveBounds(to);
    };

    const onUp = () => {
      const rs = resizeRef.current;
      resizeRef.current = null;
      setResizing(false);
      setLiveBounds(null);
      if (rs) commitMembers(rs.origin);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [resizing, commitMembers, toSlidePercent]);

  // Marquee tracking lives on window so a drag that leaves the slide still ends.
  useEffect(() => {
    if (!marquee) return undefined;

    const onMove = (e) => {
      const current = toSlidePercent(e.clientX, e.clientY);
      if (current) setMarquee((m) => (m ? { ...m, current } : m));
    };

    const onUp = () => {
      setMarquee((m) => {
        if (!m) return null;
        const rect = {
          left: Math.min(m.start.x, m.current.x),
          top: Math.min(m.start.y, m.current.y),
          width: Math.abs(m.current.x - m.start.x),
          height: Math.abs(m.current.y - m.start.y),
        };
        // A click with no meaningful drag is not a marquee — it just clears.
        if (rect.width < 1 && rect.height < 1) clear();
        else select(members.filter((mem) => isFullyEnclosed(mem.box, rect)).map((mem) => mem.id));
        return null;
      });
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [marquee, members, select, clear, toSlidePercent]);

  if (disabled) return null;

  // Members move under the pointer during a drag; the chrome follows by the same delta
  // rather than re-deriving bounds, since Redux is not updated until mouseup.
  const shownBounds = liveBounds
    || (bounds && liveDelta ? translateBox(bounds, liveDelta.dx, liveDelta.dy) : bounds);
  const midGesture = dragging || resizing;

  const marqueeRect = marquee && {
    left: `${Math.min(marquee.start.x, marquee.current.x)}%`,
    top: `${Math.min(marquee.start.y, marquee.current.y)}%`,
    width: `${Math.abs(marquee.current.x - marquee.start.x)}%`,
    height: `${Math.abs(marquee.current.y - marquee.start.y)}%`,
  };

  return (
    <div className={classes.layer} data-group-overlay="true" data-testid="group-overlay">
      {marqueeRect && <div className={classes.marquee} style={marqueeRect} />}
      {/* Outlines show from the very first modifier-click, so a single selected object
          gives feedback. The group box, handles and command bar need two. */}
      {!marquee && selectedIds.length > 0 && shownBounds && (
        <>
          {!midGesture && selected.map((m) => (
            <div
              key={`${m.id}-outline`}
              className={classes.memberOutline}
              style={{
                left: `${m.box.left}%`,
                top: `${m.box.top}%`,
                width: `${m.box.width}%`,
                height: `${m.box.height}%`,
              }}
            />
          ))}
          {selectedIds.length > 1 && <div
            className={classes.groupBox}
            data-testid="group-bounds"
            data-group-id={activeGroupId || ''}
            style={{
              left: `${shownBounds.left}%`,
              top: `${shownBounds.top}%`,
              width: `${shownBounds.width}%`,
              height: `${shownBounds.height}%`,
            }}
          />}
          {selectedIds.length > 1 && !midGesture && HANDLES.map((h) => (
            <div
              key={h.name}
              className={classes.handle}
              data-testid={`group-handle-${h.name}`}
              style={{
                left: `${shownBounds.left + shownBounds.width * h.fx}%`,
                top: `${shownBounds.top + shownBounds.height * h.fy}%`,
                cursor: h.cursor,
              }}
              onMouseDown={(e) => beginResize(e, h)}
            />
          ))}
          {selectedIds.length > 1 && !midGesture && <div
            className={classes.commandBar}
            style={{ left: `${shownBounds.left + shownBounds.width / 2}%`, top: `${shownBounds.top}%` }}
          >
            <button
              type="button"
              className={classes.command}
              data-testid={activeGroupId ? 'ungroup-button' : 'group-button'}
              // The overlay's own capture listener would otherwise read this as a
              // click on bare background and start a marquee.
              onMouseDown={(e) => e.stopPropagation()}
              onClick={activeGroupId ? doUngroup : doGroup}
            >
              {activeGroupId ? 'Ungroup' : 'Group'}
            </button>
          </div>}
        </>
      )}
    </div>
  );
};

export default GroupSelectionOverlay;
